import { requestPuppy } from '@wix/da-http-client';
import moveArrayElement from 'lodash-move';
import snakeCase from 'lodash/snakeCase';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import {
  updateLayoutVersion,
  updateModuleOrderSuccess,
  addModule,
  addModuleFail,
  addModuleSuccess,
  moveModule,
  removeModule,
  removeModuleSuccess,
} from '../actions/layout';
import { getZone } from '../selectors/zones';
import { getPageName } from '../selectors/page';
import { getVersion } from '../selectors/layout';
import { fetchModuleSuccess, updateModule } from '../actions/modules';

function* postLayout(endpoint: string, data: any) {
  return yield call(
    requestPuppy,
    {
      method: 'post',
      url: `layout/${endpoint}`,
      data,
    },
    undefined,
    'gruser'
  );
}

export function* addModuleSaga(action: ReturnType<typeof addModule>) {
  const { gruser, ...payload } = action.payload;
  if (!payload) {
    return;
  }
  const { zoneName, position, moduleName: module } = payload;

  const page = yield select(getPageName, gruser);
  const { gruserId, gruserTypeid, gruserItemid } = gruser;
  const { installedModulesIds } = yield select(getZone, gruser, zoneName);
  const afterModuleId = installedModulesIds[position - 1];
  const params = {
    gruserid: gruserId,
    gruser_typeid: gruserTypeid,
    gruser_itemid: gruserItemid,
    page,
    zone: snakeCase(zoneName as string),
    after_moduleid: afterModuleId,
    module,
  };

  const response = yield call(postLayout, 'add_module', params);
  if (response && response.module) {
    yield put(addModuleSuccess(gruser, payload));
    yield put(fetchModuleSuccess(gruser, response.module)); // augment module metadata with a special flag
    yield put(
      updateModule(gruser, {
        id: response.module.id,
        moduleMeta: { isNewlyAdded: true },
      })
    );

    const order = [...installedModulesIds];
    order.splice(position, 0, response.module.id);
    yield put(updateModuleOrderSuccess(gruser, { zoneName, order }));
    yield put(updateLayoutVersion(gruser, { version: response.layoutVersion }));
  } else {
    yield put(addModuleFail(gruser, payload));
  }
}

export function* removeModuleSaga(action) {
  const { gruser, ...payload } = action?.payload || {};
  const { zoneName, moduleId } = payload;
  const page = yield select(getPageName, gruser);
  const { gruserId, gruserTypeid, gruserItemid } = gruser;
  const version = yield select(getVersion, gruser);
  const params = {
    gruserid: gruserId,
    gruser_typeid: gruserTypeid,
    gruser_itemid: gruserItemid,
    page,
    moduleid: moduleId,
    version,
  };

  const response = yield call(postLayout, 'remove_module', params);

  if (response) {
    const { installedModulesIds } = yield select(getZone, gruser, zoneName);
    const order = installedModulesIds?.filter(id => id !== moduleId);
    yield put(updateModuleOrderSuccess(gruser, { zoneName, order }));
    yield put(removeModuleSuccess(gruser, action.payload));
    yield put(updateLayoutVersion(gruser, { version: response.layoutVersion }));
  }
}

export function* moveModuleSaga(action) {
  const { gruser, ...payload } = action?.payload || {};
  const { zoneName, moduleId, position } = payload;
  const { gruserId, gruserTypeid, gruserItemid } = gruser;
  const version = yield select(getVersion, gruser);
  const { installedModulesIds } = yield select(getZone, gruser, zoneName); // imagine the drag item is pulled up and the rest items in the stack filled the gap
  const installedModulesIdsWithoutCurrent = installedModulesIds.filter(
    id => id !== moduleId
  ); // now we can find out what module it will go after to be on the desired position
  const afterModuleId = installedModulesIdsWithoutCurrent[position - 1];
  const response = yield call(postLayout, 'move_module', {
    gruserid: gruserId,
    gruser_typeid: gruserTypeid,
    gruser_itemid: gruserItemid,
    moduleid: moduleId,
    after_moduleid: afterModuleId,
    version,
  });
  if (response) {
    const currentModuleIndex = installedModulesIds.indexOf(moduleId);
    const order = moveArrayElement(
      installedModulesIds,
      currentModuleIndex,
      position
    );
    yield put(updateModuleOrderSuccess(gruser, { zoneName, order }));

    yield put(updateLayoutVersion(gruser, { version: response.layoutVersion }));
  }
}

export default function* rootLayoutSaga() {
  yield all([
    takeEvery(addModule, addModuleSaga),
    takeEvery(removeModule, removeModuleSaga),
    takeEvery(moveModule, moveModuleSaga),
  ]);
}
